import { AnimatePresence, motion } from "framer-motion";
import { LoadingDots } from "utils/loadingAnimation";
import CodeImage from "./CodeImage";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { StepByStepInstructionsContentContext } from "components/utils/Contexts";
import { getInstructionImage } from "services/filesRequests";
import FullscreenCode from "./fullscreen-code";
import { InstructionStepImage } from "utils/interfaces";
import Content from "./Content";

interface ContentProps {
  stepByStepInstructions: any;
  currentInstructionsStep: number;
  dotsNumber?: number;
  setCurrentInstructionsStep?: React.Dispatch<React.SetStateAction<number>>;
}

const InstructionsContent: React.FC<ContentProps> = ({
  stepByStepInstructions,
  currentInstructionsStep,
  dotsNumber = 7,
  setCurrentInstructionsStep,
}) => {
  // States definition
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(currentInstructionsStep);
  // Images states
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [showCodeFullscreen, setShowCodeFullscreen] = useState(false);
  const [imageList, setImageList] = useState<{
    [key: string]: InstructionStepImage;
  }>({});
  const [activeCurrentStepImageInfo, setActiveCurrentStepImageInfo] =
    useState<InstructionStepImage>(null);
  const [activeFullCodeImageInfo, setActiveFullCodeImageInfo] =
    useState<InstructionStepImage>(null);
  const [initialCodeTab, setInitialCodeTab] = useState(0);

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  /**
   * Function used to get the instructions images
   * @returns
   */
  /**
   * Function used to get the instructions images
   * @returns
   */
  const getInstructionsData = async () => {
    // Start with changing this state
    setIsLoadingImages(true);

    // Wrap both processes into an async function
    const getInstructionImageWrap = async (
      imageInfo: InstructionStepImage,
      setCurrentImageInfo: React.Dispatch<
        React.SetStateAction<InstructionStepImage>
      >,
    ) => {
      // And check if the image is defined
      if (!imageInfo) {
        setCurrentImageInfo(null);
        return;
      }

      // Check if the hash is defined
      const hash = imageInfo.hash;
      if (!hash) {
        setCurrentImageInfo(null);
        return;
      }

      // Try to get the content
      const loadedImage = imageList[hash];

      // First of all, check if the selected step is on the images list
      if (loadedImage && loadedImage?.content) {
        // Check if the current step is defined in the imagesList for this step
        setCurrentImageInfo(loadedImage || null);
      }
      // Otherwise, check if it's undefined to load the image from AWS
      else if (
        loadedImage === undefined ||
        loadedImage?.content === undefined
      ) {
        // Get the current step image
        const image = (await getInstructionImage(
          id || "",
          moduleOrProject || "",
          hash,
        )) as string;

        // If everything went right
        if (image) {
          // Add the image to the image list
          setImageList((currentList) => {
            currentList[hash] = { ...imageInfo, content: image };
            return currentList;
          });

          // Set the state
          setCurrentImageInfo({ ...imageInfo, content: image });
        } else {
          // Otherwise set the empty images
          setCurrentImageInfo(null);
          setImageList((currentList) => {
            currentList[hash] = null;
            return currentList;
          });
        }
      }
      // But if it's not undefined, then assume that it's a null
      // variable (=== does not exist)
      else {
        setCurrentImageInfo(null);
      }
    };

    // And run both of them in parallel to fetch the data
    // more efficiently
    return Promise.all([
      getInstructionImageWrap(
        stepByStepInstructions[selectedStep].stepImage,
        setActiveCurrentStepImageInfo,
      ),
      getInstructionImageWrap(
        stepByStepInstructions[selectedStep].codeSoFarImage,
        setActiveFullCodeImageInfo,
      ),
    ]);
  };

  /**
   * Function used at the beginning of the rendering of this component
   * to wait until the images are fetched
   */
  useEffect(() => {
    getInstructionsData().then(() => {
      // Useful for the very first loading
      setIsLoading(false);
      // And for stopping the loading images waiting icon animation
      setIsLoadingImages(false);
    });
  }, [selectedStep]);

  /**
   * Function used to update the current instructions step
   * for saving the current step in the editor environment
   */
  useEffect(() => {
    if (setCurrentInstructionsStep) setCurrentInstructionsStep(selectedStep);
  }, [selectedStep]);

  return (
    <StepByStepInstructionsContentContext.Provider value={{ dotsNumber }}>
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full h-full inline-flex justify-center items-center"
          >
            <LoadingDots bgColor="bg-secondary-600" />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            className="inline-flex flex-col gap-[10px] w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CodeImage
              key={`${selectedStep}-${activeCurrentStepImageInfo?.content}-${activeFullCodeImageInfo?.content}`}
              currentStepImage={activeCurrentStepImageInfo?.content || ""}
              fullCodeImage={activeFullCodeImageInfo?.content || ""}
              isLoadingImages={isLoadingImages}
              setShowCodeFullscreen={setShowCodeFullscreen}
              setInitialCodeTab={setInitialCodeTab}
            />
            <Content
              instructions={stepByStepInstructions}
              activeFullCodeImage={activeFullCodeImageInfo?.content || ""}
              setShowCodeFullscreen={setShowCodeFullscreen}
              setInitialCodeTab={setInitialCodeTab}
              selectedIndex={selectedStep}
              setSelectedIndex={setSelectedStep}
            />
          </motion.div>
        )}
      </AnimatePresence>
      {showCodeFullscreen &&
        createPortal(
          <FullscreenCode
            currentStepImage={activeCurrentStepImageInfo}
            fullCodeImage={activeFullCodeImageInfo}
            setShowCodeFullscreen={setShowCodeFullscreen}
            initialCodeTab={initialCodeTab}
          />,
          document.body,
        )}
    </StepByStepInstructionsContentContext.Provider>
  );
};

export default InstructionsContent;
